import { type FormEvent, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { FenceMode, LocationUnavailableMode } from "../../shared/policy";
import type { BusinessSettings, Person, Site } from "../../shared/types";
import { Dialog, ErrorBanner, Field, Loading } from "../components/ui";
import { del, get, patch, post } from "../lib/api";
import { googleMapsLink } from "../lib/maps";
import { useApi } from "../lib/useApi";
import {
  Check,
  ConfirmDialog,
  changedFields,
  orNull,
  PageHeader,
  RadioCards,
  ReasonDialog,
  toApiError,
  useConfig,
  usePeople,
  useRecordForm,
  useSettings,
} from "./common";
import { SiteMap } from "./SiteMap";

interface SiteForm {
  name: string;
  address: string;
  postcode: string;
  lat: string;
  lng: string;
  radiusM: number;
  fenceMode: "" | FenceMode;
  locationUnavailableMode: "" | LocationUnavailableMode;
  qbCustomerName: string;
  qbCustomerId: string;
  notes: string;
  active: boolean;
}

const MIN_RADIUS = 20;
const MAX_RADIUS = 2000;
const SLIDER_MAX = 500;

function toForm(s: Site | null, defaultRadius: number): SiteForm {
  return {
    name: s?.name ?? "",
    address: s?.address ?? "",
    postcode: s?.postcode ?? "",
    lat: s ? String(s.lat) : "",
    lng: s ? String(s.lng) : "",
    radiusM: s?.radiusM ?? defaultRadius,
    fenceMode: s?.fenceMode ?? "",
    locationUnavailableMode: s?.locationUnavailableMode ?? "",
    qbCustomerName: s?.qbCustomerName ?? "",
    qbCustomerId: s?.qbCustomerId ?? "",
    notes: s?.notes ?? "",
    active: s?.active ?? true,
  };
}

/** The API body for a form (all fields; the caller diffs for updates). */
function toPayload(f: SiteForm) {
  return {
    name: f.name.trim(),
    address: orNull(f.address),
    postcode: orNull(f.postcode),
    lat: Number(f.lat),
    lng: Number(f.lng),
    radiusM: f.radiusM,
    fenceMode: f.fenceMode || null,
    locationUnavailableMode: f.locationUnavailableMode || null,
    qbCustomerName: orNull(f.qbCustomerName),
    qbCustomerId: orNull(f.qbCustomerId),
    notes: orNull(f.notes),
    active: f.active,
  };
}

const validCoords = (f: SiteForm) => {
  const lat = Number(f.lat);
  const lng = Number(f.lng);
  return f.lat.trim() !== "" && f.lng.trim() !== "" && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
    ? { lat, lng }
    : null;
};

const FENCE_TEXT = { block: "Block", flag: "Flag" } as const;
const UNAVAILABLE_TEXT = { approval: "Needs approval", flag: "Flag" } as const;

function QrSection({ site, onRegenerated }: { site: Site; onRegenerated: (version: number) => void }) {
  const [confirming, setConfirming] = useState(false);
  return (
    <section className="card stack adm-section">
      <h2>QR sticker</h2>
      <p>
        Current code: <strong>version {site.qrVersion}</strong>. Only the sticker printed with this version
        works.
      </p>
      <div className="row">
        <Link to={`/admin/sites/print?ids=${site.id}`} className="btn">
          Print sticker
        </Link>
        <button className="btn danger" onClick={() => setConfirming(true)}>
          Regenerate code
        </button>
      </div>
      <p className="muted small">
        Regenerate only if the sticker has been lost, copied or photographed by someone who shouldn't have it.
      </p>
      {confirming && (
        <ReasonDialog
          title="Regenerate QR code?"
          intro={
            <div className="banner warn">
              The current sticker at <strong>{site.name}</strong> will stop working immediately. Cleaners
              won't be able to scan in or out there until you print and put up the new sticker.
            </div>
          }
          placeholder="e.g. Sticker damaged"
          confirmLabel="Regenerate code"
          danger
          required={false}
          onConfirm={async (reason) => {
            const r = await post<{ version: number }>(`/admin/sites/${site.id}/regenerate-qr`, {
              reason: reason || null,
            });
            onRegenerated(r.version);
          }}
          onClose={() => setConfirming(false)}
        />
      )}
    </section>
  );
}

function AssignedCleaners({ siteId, people }: { siteId: string; people: Person[] | undefined }) {
  if (!people) return <p className="muted">Loading…</p>;
  const assigned = people.filter((p) => p.siteIds.includes(siteId));
  return (
    <section className="card stack adm-section">
      <h2>Assigned cleaners</h2>
      {assigned.length ? (
        <ul className="list">
          {assigned.map((p) => (
            <li key={p.id}>
              <Link to={`/admin/people/${p.id}`}>{p.name}</Link>
              {!p.active && <span className="badge"> Inactive</span>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No one is assigned to this site yet.</p>
      )}
      <p className="muted small">Change assignments on each cleaner's page.</p>
    </section>
  );
}

function SiteEditor({
  site,
  settings,
  mapsKey,
  onSaved,
}: {
  site: Site | null;
  settings: BusinessSettings;
  mapsKey: string | null;
  onSaved: (s: Site, created: boolean) => void;
}) {
  const defaultRadius = settings.defaultRadiusM;
  const [form, setForm] = useRecordForm<Site | null, SiteForm>(site, site?.id ?? "new", (s) =>
    toForm(s, defaultRadius),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finding, setFinding] = useState(false);
  const [findMsg, setFindMsg] = useState<{ kind: "ok" | "danger"; text: string } | null>(null);
  const set = <K extends keyof SiteForm>(k: K, v: SiteForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const coords = validCoords(form);

  async function find() {
    if (!form.postcode.trim()) return setFindMsg({ kind: "danger", text: "Type a postcode first." });
    setFinding(true);
    setFindMsg(null);
    try {
      const r = await get<{ postcode: string; lat: number; lng: number }>(
        `/admin/geocode?postcode=${encodeURIComponent(form.postcode.trim())}`,
      );
      setForm((f) => ({ ...f, postcode: r.postcode, lat: r.lat.toFixed(6), lng: r.lng.toFixed(6) }));
      setFindMsg({
        kind: "ok",
        text: mapsKey
          ? "Pin placed at the postcode centre. Drag it to the entrance you use."
          : "Location set to the postcode centre. Check it in Google Maps and adjust if needed.",
      });
    } catch (e) {
      setFindMsg({ kind: "danger", text: toApiError(e).message });
    } finally {
      setFinding(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) return setError("Give the site a name.");
    if (!coords) return setError("Set the site's location: use Find with the postcode, or enter it.");
    if (form.radiusM < MIN_RADIUS || form.radiusM > MAX_RADIUS)
      return setError(`Radius must be between ${MIN_RADIUS} and ${MAX_RADIUS} metres.`);
    setBusy(true);
    try {
      const payload = toPayload(form);
      if (site) {
        const changes = changedFields(toPayload(toForm(site, settings.defaultRadiusM)), payload);
        if (!Object.keys(changes).length) {
          setBusy(false);
          return setError("Nothing has changed.");
        }
        const saved = await patch<Site>(`/admin/sites/${site.id}`, changes);
        setForm(toForm(saved, settings.defaultRadiusM));
        onSaved(saved, false);
      } else {
        onSaved(await post<Site>("/admin/sites", payload), true);
      }
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  }

  const defaultFence = FENCE_TEXT[settings.fenceMode];
  const defaultUnavailable = UNAVAILABLE_TEXT[settings.locationUnavailableMode];

  return (
    <form className="stack" onSubmit={submit}>
      <section className="card stack">
        <h2>Details</h2>
        <Field label="Name" hint="Shown to cleaners and printed on the sticker.">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} required />
        </Field>
        <Field label="Address">
          <input value={form.address} onChange={(e) => set("address", e.target.value)} maxLength={300} />
        </Field>
        <Field label="Postcode">
          <div className="row" style={{ flexWrap: "nowrap" }}>
            <input
              value={form.postcode}
              onChange={(e) => set("postcode", e.target.value)}
              maxLength={10}
              autoCapitalize="characters"
              style={{ maxWidth: 180 }}
            />
            <button type="button" className="btn" onClick={find} disabled={finding}>
              {finding ? "Finding…" : "Find"}
            </button>
          </div>
        </Field>
        {findMsg && <div className={`banner ${findMsg.kind}`}>{findMsg.text}</div>}
      </section>

      <section className="card stack">
        <h2>Location and geofence</h2>
        {mapsKey ? (
          <>
            <p className="muted small">
              Drag the pin (or tap the map) to the entrance cleaners use. The green circle is the geofence.
            </p>
            <SiteMap
              apiKey={mapsKey}
              position={coords}
              radiusM={form.radiusM}
              onMove={(p) => setForm((f) => ({ ...f, lat: p.lat.toFixed(6), lng: p.lng.toFixed(6) }))}
            />
            {coords && (
              <p className="muted small">
                {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="grid cols-2">
              <Field label="Latitude">
                <input
                  value={form.lat}
                  inputMode="decimal"
                  onChange={(e) => set("lat", e.target.value)}
                  placeholder="e.g. 51.504500"
                />
              </Field>
              <Field label="Longitude">
                <input
                  value={form.lng}
                  inputMode="decimal"
                  onChange={(e) => set("lng", e.target.value)}
                  placeholder="e.g. -0.086500"
                />
              </Field>
            </div>
            {coords && (
              <a href={googleMapsLink(coords.lat, coords.lng)} target="_blank" rel="noreferrer">
                Open in Google Maps
              </a>
            )}
            <p className="muted small">
              To drag a pin on a map here, set the <code>GOOGLE_MAPS_API_KEY</code> secret on the server.
              Without it, find the entrance in Google Maps, right-click it and copy the numbers into these
              boxes.
            </p>
          </>
        )}
        <Field
          label="Radius"
          hint="How far from the pin a phone can be and still count as on site. 100 m suits most buildings; use more for large sites or poor signal."
        >
          <div className="adm-radius">
            <input
              type="range"
              min={MIN_RADIUS}
              max={SLIDER_MAX}
              step={5}
              value={Math.min(form.radiusM, SLIDER_MAX)}
              onChange={(e) => set("radiusM", Number(e.target.value))}
              aria-label="Radius slider"
            />
            <input
              type="number"
              inputMode="numeric"
              min={MIN_RADIUS}
              max={MAX_RADIUS}
              value={form.radiusM}
              onChange={(e) => set("radiusM", Number(e.target.value))}
              aria-label="Radius in metres"
            />
            <span className="muted">m</span>
          </div>
        </Field>
      </section>

      <section className="card stack">
        <h2>Rules for this site</h2>
        <RadioCards
          legend="Clocking in or out from outside the geofence"
          name="fenceMode"
          value={form.fenceMode}
          onChange={(v) => set("fenceMode", v)}
          columns
          options={[
            {
              value: "",
              title: `Use business default (${defaultFence})`,
              description: "Follows whatever is chosen in Settings.",
            },
            {
              value: "block",
              title: "Block",
              description: "It's stopped, and the cleaner can send it to you for approval instead.",
            },
            {
              value: "flag",
              title: "Flag",
              description: "It's accepted but marked so you can check it later.",
            },
          ]}
        />
        <RadioCards
          legend="When the phone can't give a good location"
          name="locationUnavailableMode"
          value={form.locationUnavailableMode}
          onChange={(v) => set("locationUnavailableMode", v)}
          columns
          options={[
            {
              value: "",
              title: `Use business default (${defaultUnavailable})`,
              description: "Follows whatever is chosen in Settings.",
            },
            {
              value: "approval",
              title: "Needs approval",
              description: "The time is kept but only counts once you approve it.",
            },
            {
              value: "flag",
              title: "Flag",
              description: "It's accepted but marked “Location unavailable” for you to check.",
            },
          ]}
        />
      </section>

      <section className="card stack">
        <h2>QuickBooks and notes</h2>
        <div className="grid cols-2">
          <Field label="QuickBooks customer name" hint="Must match the customer name in QuickBooks exactly.">
            <input
              value={form.qbCustomerName}
              onChange={(e) => set("qbCustomerName", e.target.value)}
              maxLength={200}
            />
          </Field>
          <Field label="QuickBooks customer ID" hint="Only needed for sending time straight to QuickBooks.">
            <input
              value={form.qbCustomerId}
              onChange={(e) => set("qbCustomerId", e.target.value)}
              maxLength={64}
            />
          </Field>
        </div>
        <Field label="Notes" hint="For admins only, e.g. key safe or alarm details.">
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} maxLength={1000} />
        </Field>
        <Check
          label="Active (cleaners can clock in here)"
          checked={form.active}
          onChange={(v) => set("active", v)}
        />
      </section>

      <div className="adm-form-actions">
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? "Saving…" : site ? "Save changes" : "Add site"}
        </button>
        {error && (
          <span className="banner danger small" role="alert">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}

/**
 * Delete (no history) or archive/restore (has history). Visits are pay records that must
 * be kept, so a site that has any can only be archived.
 */
function RemoveSite({
  site,
  onChanged,
  onDeleted,
}: {
  site: Site;
  onChanged: (s: Site) => void;
  onDeleted: () => void;
}) {
  const [dialog, setDialog] = useState<"delete" | "archive" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const visits = site.visitCount;

  async function restore() {
    setBusy(true);
    setError(null);
    try {
      onChanged(await patch<Site>(`/admin/sites/${site.id}`, { active: true }));
    } catch (e) {
      setError(toApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack adm-section">
      <h2>Remove this site</h2>
      {!site.active ? (
        <>
          <p className="muted small">
            This site is archived: cleaners can't see it and its sticker doesn't work. Its {visits} visit
            {visits === 1 ? "" : "s"} stay in timesheets and exports.
          </p>
          <div className="row">
            <button className="btn" onClick={restore} disabled={busy}>
              {busy ? "Restoring…" : "Restore site"}
            </button>
          </div>
        </>
      ) : visits === 0 ? (
        <>
          <p className="muted small">Nobody has clocked in here yet, so this site can be deleted for good.</p>
          <div className="row">
            <button className="btn danger" onClick={() => setDialog("delete")}>
              Delete site
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="muted small">
            This site has {visits} visit{visits === 1 ? "" : "s"} on record. Clock-in records are kept for
            payroll, so it can't be deleted — archive it instead. Archived sites disappear from cleaners'
            lists and their sticker stops working; the records stay, and you can restore the site at any time.
          </p>
          <div className="row">
            <button className="btn danger" onClick={() => setDialog("archive")}>
              Archive site
            </button>
          </div>
        </>
      )}
      {error && <div className="banner danger">{error}</div>}
      {dialog === "delete" && (
        <ConfirmDialog
          title={`Delete ${site.name}?`}
          confirmLabel="Delete site"
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await del(`/admin/sites/${site.id}`);
            onDeleted();
          }}
        >
          <p>This removes the site, its cleaner assignments and its schedules. It can't be undone.</p>
        </ConfirmDialog>
      )}
      {dialog === "archive" && (
        <ConfirmDialog
          title={`Archive ${site.name}?`}
          confirmLabel="Archive site"
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => onChanged(await patch<Site>(`/admin/sites/${site.id}`, { active: false }))}
        >
          <p>
            Cleaners won't see it any more and its sticker will stop working. Its visits stay in timesheets
            and exports. You can restore it later.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

export function SitePage() {
  const { id = "" } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const site = useApi<Site>(isNew ? null : `/admin/sites/${encodeURIComponent(id)}`);
  const settings = useSettings();
  const config = useConfig();
  const people = usePeople();
  const [message, setMessage] = useState<string | null>(null);
  const [regenerated, setRegenerated] = useState<number | null>(null);

  const back = { to: "/admin/sites", label: "Sites" };
  const err = site.error ?? settings.error;
  if ((!isNew && !site.data) || !settings.data || (!config.data && !config.error)) {
    return (
      <div className="page">
        <PageHeader title={isNew ? "Add site" : "Site"} back={back} />
        {err ? (
          <ErrorBanner
            error={err}
            onRetry={() => {
              void site.reload();
              void settings.reload();
            }}
          />
        ) : (
          <Loading />
        )}
      </div>
    );
  }

  const current = isNew ? null : site.data!;
  return (
    <div className="page">
      <PageHeader
        title={current ? current.name : "Add site"}
        sub={current && !current.active ? <span className="badge">Inactive</span> : undefined}
        back={back}
      />
      {message && <div className="banner ok">{message}</div>}
      <div className="stack">
        <SiteEditor
          site={current}
          settings={settings.data}
          mapsKey={config.data?.googleMapsApiKey ?? null}
          onSaved={(s, created) => {
            setMessage(created ? "Site added. Print its QR sticker below." : "Changes saved.");
            window.scrollTo({ top: 0, behavior: "smooth" });
            if (created) navigate(`/admin/sites/${s.id}`, { replace: true });
            else site.setData(s);
          }}
        />
        {current && (
          <>
            {regenerated && (
              <Dialog title="New code ready" onClose={() => setRegenerated(null)}>
                <p>
                  {current.name} now uses code version {regenerated}. The old sticker no longer works. Print
                  the new sticker and replace it as soon as you can.
                </p>
                <div className="row">
                  <Link to={`/admin/sites/print?ids=${current.id}`} className="btn primary">
                    Print sticker
                  </Link>
                  <button className="btn" onClick={() => setRegenerated(null)}>
                    Later
                  </button>
                </div>
              </Dialog>
            )}
            <QrSection
              site={current}
              onRegenerated={(v) => {
                setRegenerated(v);
                void site.reload();
              }}
            />
            <AssignedCleaners siteId={current.id} people={people.data} />
            <RemoveSite
              site={current}
              onChanged={(s) => {
                site.setData(s);
                setMessage(s.active ? "Site restored." : "Site archived.");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              onDeleted={() => navigate("/admin/sites", { replace: true })}
            />
          </>
        )}
      </div>
    </div>
  );
}
